import React, { useState } from 'react';
import './SemanticColorTest.css';

const SemanticColorTest = () => {
  const [formState, setFormState] = useState({
    successInput: '',
    warningInput: '',
    errorInput: '',
    infoInput: ''
  });

  const handleInputChange = (field, value) => {
    setFormState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="semantic-color-test">
      <div className="test-container">
        <h2 className="test-title">Semantic Color System Test</h2>
        <p className="test-description">
          This page demonstrates the semantic color system with proper contrast ratios 
          and accessibility compliance for success, warning, error, and info states.
        </p>

        {/* Alert Components */}
        <section className="test-section">
          <h3>Alert Components</h3>
          <div className="alert-grid">
            <div className="alert-success">
              <strong>Success:</strong> Your changes have been saved successfully!
            </div>
            <div className="alert-warning">
              <strong>Warning:</strong> Please review your input before proceeding.
            </div>
            <div className="alert-error">
              <strong>Error:</strong> Unable to process your request. Please try again.
            </div>
            <div className="alert-info">
              <strong>Info:</strong> New features are available in your dashboard.
            </div>
          </div>
        </section>

        {/* Button Components */}
        <section className="test-section">
          <h3>Button Components</h3>
          <div className="button-grid">
            <button className="button-success">Success Action</button>
            <button className="button-warning">Warning Action</button>
            <button className="button-error">Error Action</button>
            <button className="button-info">Info Action</button>
          </div>
        </section>

        {/* Badge Components */}
        <section className="test-section">
          <h3>Badge Components</h3>
          <div className="badge-grid">
            <span className="badge-success">Active</span>
            <span className="badge-warning">Pending</span>
            <span className="badge-error">Failed</span>
            <span className="badge-info">New</span>
          </div>
        </section>

        {/* Status Indicators */}
        <section className="test-section">
          <h3>Status Indicators</h3>
          <div className="status-grid">
            <div className="status-success">Operation completed</div>
            <div className="status-warning">Requires attention</div>
            <div className="status-error">Operation failed</div>
            <div className="status-info">Additional information</div>
          </div>
        </section>

        {/* Form Input States */}
        <section className="test-section">
          <h3>Form Input States</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="success-input">Success State</label>
              <input
                id="success-input"
                type="text"
                className="input-success"
                placeholder="Valid input"
                value={formState.successInput}
                onChange={(e) => handleInputChange('successInput', e.target.value)}
                aria-describedby="success-help"
              />
              <div id="success-help" className="text-success">Input is valid</div>
            </div>

            <div className="form-group">
              <label htmlFor="warning-input">Warning State</label>
              <input
                id="warning-input"
                type="text"
                className="input-warning"
                placeholder="Input with warning"
                value={formState.warningInput}
                onChange={(e) => handleInputChange('warningInput', e.target.value)}
                aria-describedby="warning-help"
              />
              <div id="warning-help" className="text-warning">Please double-check this input</div>
            </div>

            <div className="form-group">
              <label htmlFor="error-input">Error State</label>
              <input
                id="error-input"
                type="text"
                className="input-error"
                placeholder="Invalid input"
                value={formState.errorInput}
                onChange={(e) => handleInputChange('errorInput', e.target.value)}
                aria-describedby="error-help"
                aria-invalid="true"
              />
              <div id="error-help" className="text-error" role="alert">This field is required</div>
            </div>

            <div className="form-group">
              <label htmlFor="info-input">Info State</label>
              <input
                id="info-input"
                type="text"
                className="input-info"
                placeholder="Input with info"
                value={formState.infoInput}
                onChange={(e) => handleInputChange('infoInput', e.target.value)}
                aria-describedby="info-help"
              />
              <div id="info-help" className="text-info">Additional information about this field</div>
            </div>
          </div>
        </section>

        {/* Text Color Utilities */}
        <section className="test-section">
          <h3>Text Color Utilities</h3>
          <div className="text-grid">
            <p className="text-success">Success text color</p>
            <p className="text-success-light">Light success text</p>
            <p className="text-success-dark">Dark success text</p>
            
            <p className="text-warning">Warning text color</p>
            <p className="text-warning-light">Light warning text</p>
            <p className="text-warning-dark">Dark warning text</p>
            
            <p className="text-error">Error text color</p>
            <p className="text-error-light">Light error text</p>
            <p className="text-error-dark">Dark error text</p>
            
            <p className="text-info">Info text color</p>
            <p className="text-info-light">Light info text</p>
            <p className="text-info-dark">Dark info text</p>
          </div>
        </section>

        {/* Background Color Utilities */}
        <section className="test-section">
          <h3>Background Color Utilities</h3>
          <div className="bg-grid">
            <div className="bg-success-light color-demo">Success Light BG</div>
            <div className="bg-warning-light color-demo">Warning Light BG</div>
            <div className="bg-error-light color-demo">Error Light BG</div>
            <div className="bg-info-light color-demo">Info Light BG</div>
          </div>
        </section>

        {/* Border Color Utilities */}
        <section className="test-section">
          <h3>Border Color Utilities</h3>
          <div className="border-grid">
            <div className="border-demo border-success">Success Border</div>
            <div className="border-demo border-warning">Warning Border</div>
            <div className="border-demo border-error">Error Border</div>
            <div className="border-demo border-info">Info Border</div>
          </div>
        </section>

        {/* Accessibility Information */}
        <section className="test-section">
          <h3>Accessibility Information</h3>
          <div className="accessibility-info">
            <p>All semantic color combinations meet WCAG 2.1 AA contrast requirements:</p>
            <ul>
              <li>Normal text: Minimum 4.5:1 contrast ratio</li>
              <li>Large text: Minimum 3:1 contrast ratio</li>
              <li>Interactive elements have proper focus indicators</li>
              <li>High contrast mode support included</li>
              <li>Screen reader compatible with proper ARIA attributes</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SemanticColorTest;